import React, { useRef } from "react";
import StatusSelect from "@/components/ui/StatusSelect";
import { Interview } from "@/services/interviewService";
import { Input } from "@/components/ui/input";
import { formatDate, formatCurrency, getStatusBadge } from "./utils";
import { DateTimePicker } from "@/components/ui/date-time-picker";

interface EditableCellProps {
  interview: Interview;
  field: keyof Interview;
  isEditing: boolean;
  handleInputChange: (
    id: string,
    field: keyof Interview,
    value: string
  ) => void;
  handleKeyDown: (
    e: React.KeyboardEvent,
    interview: Interview,
    field: keyof Interview
  ) => void;
  handleBlur: (id: string, field: keyof Interview) => void;
  inputRef: React.RefObject<HTMLInputElement>;
  // Optional inline autocomplete suggestion. Should be the full suggested string.
  inlineSuggestion?: string;
}

const EditableCell: React.FC<EditableCellProps> = ({
  interview,
  field,
  isEditing,
  handleInputChange,
  handleKeyDown,
  handleBlur,
  inputRef,
  inlineSuggestion,
}) => {
  const handleAutoSelect = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  if (isEditing) {
    if (field === "status") {
      return (
        <StatusSelect
          value={interview.status as any}
          autoOpen
          onChange={(newStatus) => {
            handleInputChange(interview.id, field, newStatus);
            handleBlur(interview.id, field);
          }}
          onBlur={() => handleBlur(interview.id, field)}
        />
      );
    } else if (field === "date") {
      // Parse the date from the interview - handle null/undefined/empty dates
      let dateValue: Date | undefined;

      if (interview.date) {
        if (interview.date instanceof Date) {
          dateValue = interview.date;
        } else if (
          typeof interview.date === "string" &&
          interview.date.trim() !== ""
        ) {
          const parsedDate = new Date(interview.date);
          // Only use the date if it's valid
          if (!isNaN(parsedDate.getTime())) {
            dateValue = parsedDate;
          }
        }
      }

      // Handle setting the date through the DateTimePicker
      const handleDateChange = (newDate: Date | undefined) => {
        if (newDate) {
          // Convert to ISO string for consistent storage
          const isoString = newDate.toISOString();

          // Important: Update the interview object in the parent component
          // This ensures the new value is stored immediately
          handleInputChange(interview.id, field, isoString);
        }
      };

      // Handle keydown events for the date picker
      const handleDateKeyDown = (e: React.KeyboardEvent) => {
        // We just forward the event to our parent handler
        handleKeyDown(e, interview, field);
      };

      // Special blur handler for date field to save changes when explicitly requested
      // This is called by DateTimePicker when Done is clicked or Tab/Enter is pressed
      const handleDateBlur = () => {
        // Call the blur handler to finalize the edit
        handleBlur(interview.id, field);
      };

      return (
        <DateTimePicker
          date={dateValue}
          setDate={handleDateChange}
          onBlur={handleDateBlur}
          onKeyDown={handleDateKeyDown}
          autoFocus={false}
        />
      );
    } else if (
      field === "initialInterviewCost" ||
      field === "duration" ||
      field === "questionnaireCost"
    ) {
      return (
        <Input
          ref={inputRef}
          id={`${interview.id}-${field}`}
          type="text" // Changed from "number" to "text" to remove arrows
          value={interview[field]}
          onChange={(e) =>
            handleInputChange(interview.id, field, e.target.value)
          }
          onKeyDown={(e) => handleKeyDown(e, interview, field)}
          onBlur={() => handleBlur(interview.id, field)}
          className="w-full h-9 min-h-9 px-2 py-1 focus-visible:ring-0 focus-visible:ring-offset-0"
          onFocus={handleAutoSelect}
        />
      );
    } else if (field === "angle" || field === "network") {
      // Inline autocomplete input with suggestion overlay
      return (
        <div className="relative w-full h-10 min-h-10">
          {/* Suggestion overlay */}
          {inlineSuggestion && (
            <div
              aria-hidden
              className="absolute inset-[1px] flex items-center px-3 py-2 text-base md:text-sm pointer-events-none select-none overflow-hidden whitespace-nowrap"
              style={{ lineHeight: "1.5rem" }}
            >
              <span className="text-transparent">
                {interview[field] as string}
              </span>
              <span className="text-muted-foreground">{inlineSuggestion}</span>
            </div>
          )}

          {/* Actual input */}
          <Input
            ref={inputRef}
            id={`${interview.id}-${field}`}
            type="text"
            value={interview[field] as string}
            onChange={(e) =>
              handleInputChange(interview.id, field, e.target.value)
            }
            onKeyDown={(e) => handleKeyDown(e, interview, field)}
            onBlur={() => handleBlur(interview.id, field)}
            className="w-full bg-transparent relative z-10 h-10 min-h-10 px-3 py-2 text-base md:text-sm leading-[1.5rem] focus-visible:ring-0 focus-visible:ring-offset-0"
            style={{ position: "relative" }}
            autoFocus
          />
        </div>
      );
    } else {
      return (
        <Input
          ref={inputRef}
          id={`${interview.id}-${field}`}
          type="text"
          value={interview[field] as string}
          onChange={(e) =>
            handleInputChange(interview.id, field, e.target.value)
          }
          onKeyDown={(e) => handleKeyDown(e, interview, field)}
          onBlur={() => handleBlur(interview.id, field)}
          className="w-full h-9 min-h-9 px-2 py-1 focus-visible:ring-0 focus-visible:ring-offset-0"
          autoFocus
        />
      );
    }
  } else {
    if (field === "date") {
      return (
        <div className="min-h-[36px] flex items-center">
          {formatDate(interview.date)}
        </div>
      );
    } else if (
      field === "initialInterviewCost" ||
      field === "questionnaireCost"
    ) {
      return (
        <div className="min-h-[36px] flex items-center justify-end">
          {formatCurrency(interview[field] as string)}
        </div>
      );
    } else if (field === "status") {
      return (
        <div className="min-h-[36px] flex items-center">
          {getStatusBadge(interview.status)}
        </div>
      );
    } else if (field === "duration") {
      return (
        <div className="min-h-[36px] flex items-center justify-end">
          {interview[field]}
        </div>
      );
    } else {
      return (
        <div className="min-h-[36px] flex items-center">{interview[field]}</div>
      );
    }
  }
};

export default EditableCell;
